import * as CryptoJS from 'crypto-js';

import { AwsSignatureInputData } from './aws-signature-input.model';
import {Injectable} from "@angular/core";

/**
 * Amazon web services (AWS) Signature version 4 - EC2 API requests signing tool.
 * @class AwsSignature
 */
@Injectable()
export class AwsSignatureService {
  public credentials: any;

  constructor() {}

  /**
   * Generates the signature
   *
   * @param {AwsSignatureInputData} input - structure with data to be signed and keys
   * @param {Date} currentDate - optional parameter to pass custom date
   */
  public generateSignature(input: AwsSignatureInputData, currentDate: Date = new Date()): any {
    if (!input) {
      return {};
    }
    input.accessKey = this.credentials.AccessKeyId;
    input.secretKey = this.credentials.SecretAccessKey;
    input.sessionToken = this.credentials.SessionToken;
    input.host = this.credentials.host;
    input.region = "ca-central-1";
    input.canonicalUri =  input.canonicalUri || this.credentials.path;

    let { canonicalHeaders, dateStamp, amzDate } =
      this.prepareCanonicalHeaders(currentDate, input);
    console.log("Canoncical headers: " + canonicalHeaders);
    let { canonicalRequest, signedHeaders } =
      this.prepareCanonicalRequest(input, canonicalHeaders);
    console.log("Canoncical request: " + canonicalRequest);
    let { stringToSign, algorithm, credentialScope } =
      this.generateStringToSign(dateStamp, input, amzDate, canonicalRequest);
    console.log("String to sign: " + stringToSign);
    let signature = this.signString(input, dateStamp, stringToSign);
    let authorizationHeader = this.generateAuthorizationHeader(
      algorithm, input, credentialScope, signedHeaders, signature);

    let headers =  {
      'X-Amz-Date': amzDate,
      'X-Amz-Security-Token': input.sessionToken || "",
      'Authorization': authorizationHeader
    };


    if(["POST","PATCH","PUT"].includes(input.method)) {
      headers['Content-Type'] = input.contentType || "application/json";
    }
    return headers;
  }

  private generateAuthorizationHeader(algorithm: string, input: AwsSignatureInputData,
                                      credentialScope: string, signedHeaders: string, signature: any)
  {
    return algorithm + ' ' + 'Credential=' + input.accessKey + '/'
      + credentialScope + ', ' + 'SignedHeaders=' + signedHeaders
      + ', ' + 'Signature=' + signature;
  }

  private signString(input: AwsSignatureInputData, dateStamp: string, stringToSign: string) {
    let signingKey = this.getSignatureKey(input.secretKey, dateStamp, input.region, input.service);
    let signature = CryptoJS.HmacSHA256(stringToSign, signingKey).toString();
    return signature;
  }

  private generateStringToSign(dateStamp: string, input: AwsSignatureInputData, amzDate: string,
                               canonicalRequest: string)
  {
    let algorithm = 'AWS4-HMAC-SHA256';
    let credentialScope = dateStamp + '/' + input.region + '/'
      + input.service + '/' + 'aws4_request';
    let stringToSign = algorithm + '\n' + amzDate + '\n' + credentialScope +
      '\n' + CryptoJS.SHA256(canonicalRequest).toString();
    return { stringToSign, algorithm, credentialScope };
  }

  private prepareCanonicalRequest(input: AwsSignatureInputData, canonicalHeaders: string) {
    let signedHeaders = 'host;x-amz-date';
    if( ["POST","PATCH","PUT"].includes(input.method) ) {
      signedHeaders = 'content-type;' + signedHeaders;
    }
    let payloadHash = CryptoJS.SHA256(input.body || input.requestParameters).toString();
    let canonicalRequest = input.method + '\n' + input.canonicalUri + '\n'
      + input.canonicalQuerystring + '\n' + canonicalHeaders + '\n'
      + signedHeaders + '\n' + payloadHash;
    return { canonicalRequest, signedHeaders };
  }

  private prepareCanonicalHeaders(currentDate: Date, input: AwsSignatureInputData) {
    let amzDate = currentDate.toISOString().replace(/-|:|\..{3}/g, '');
    let dateStamp = amzDate.substr(0, 8);
    let canonicalHeaders = 'host:'
      + input.host + '\n' + 'x-amz-date:' + amzDate + '\n';

    if( ["POST","PATCH","PUT"].includes(input.method) ) {
      canonicalHeaders = 'content-type:' + (input.contentType || "application/json") + '\n' + canonicalHeaders;
    }
    return { canonicalHeaders, dateStamp, amzDate };
  }

  private getSignatureKey(
    key: string,
    dateStamp: string,
    regionName: string,
    serviceName: string): any
  {
    var kDate = CryptoJS.HmacSHA256(dateStamp, "AWS4" + key);
    var kRegion = CryptoJS.HmacSHA256(regionName, kDate);
    var kService = CryptoJS.HmacSHA256(serviceName, kRegion);
    var kSigning = CryptoJS.HmacSHA256("aws4_request", kService);
    return kSigning;
  }
}
